import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  AccountTree as PedigreeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  ArrowBack as ArrowBackIcon,
  GetApp as ExportIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  Share as ShareIcon,
  PictureAsPdf as FullPedigreeIcon
} from '@mui/icons-material';
import DatabaseService from '../services/database';
import PedigreeExporter from '../services/pedigreeExporter';
import WebApi from '../services/webApi';
import { IS_WEB } from '../services/platform';
import { loadPdfInReservedTab, reservePdfTab } from '../utils/pdfWindow';
import { sexAvatarSx } from '../utils/sexStyle';
import { getBirdListLabels } from '../utils/birdSearch';
import RaceResults from '../components/RaceResults';
import { useLicense } from '../context/LicenseContext';

// A parent (sire/dam) card: shows the parent's name and ring details and links
// through to that bird. Falls back to the ring number when the parent has no
// name, and to "Unnamed Bird" when neither is available.
const ParentCard = ({ role, sex, name, ringNumber, onView }) => {
  const { displayName, ringLabel } = getBirdListLabels({ name, ring_number: ringNumber });
  return (
    <Card
      variant="outlined"
      onClick={onView}
      sx={{ cursor: 'pointer', height: '100%', '&:hover': { borderColor: 'primary.main' } }}
    >
      <CardContent>
        <Box display="flex" alignItems="center">
          <Avatar sx={{ mr: 2, ...sexAvatarSx(sex) }}>
            {sex === 'male' ? <MaleIcon /> : <FemaleIcon />}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
              {role}
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold" noWrap>
              {displayName}
            </Typography>
            {ringLabel && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {ringLabel}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const BirdProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state: licenseState, openPrompt } = useLicense();
  const raceReadOnly = licenseState?.mode === 'readonly' || licenseState?.mode === 'unlicensed';
  // Sharing is a web-only, paid feature.
  const canShare = licenseState?.mode === 'full' || licenseState?.mode === 'past_due';
  const [bird, setBird] = useState(null);
  const [offspring, setOffspring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [fullPedigreeAvailable, setFullPedigreeAvailable] = useState(false);
  const [openingFullPedigree, setOpeningFullPedigree] = useState(false);
  const [fullPedigreeError, setFullPedigreeError] = useState('');
  // Share-by-email dialog.
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareDone, setShareDone] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadBirdData = async () => {
      setLoading(true);
      setBird(null);
      setOffspring([]);
      setFullPedigreeAvailable(false);
      setFullPedigreeError('');

      try {
        const [birdData, offspringData, pdfHistory] = await Promise.all([
          DatabaseService.getBirdById(id),
          DatabaseService.getOffspring(id),
          IS_WEB
            ? WebApi.getPdfHistory(id).catch((error) => {
                console.error('Error checking PDF history:', error);
                return [];
              })
            : Promise.resolve([])
        ]);

        if (cancelled) return;
        setBird(birdData);
        setOffspring(offspringData);
        setFullPedigreeAvailable(
          pdfHistory.some((entry) => Number(entry.bird_id) === Number(id) && entry.available)
        );
      } catch (error) {
        if (!cancelled) console.error('Error loading bird data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (id) loadBirdData();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleDelete = async () => {
    try {
      await DatabaseService.deleteBird(id);
      navigate('/birds');
    } catch (error) {
      console.error('Error deleting bird:', error);
      setDeleteDialog(false);
      if (error.isLicenseError) {
        window.alert(error.message);
      }
    }
  };

  const handleDuplicateForPedigree = () => {
    // Navigate to add bird with shared details pre-filled. Only pass the
    // fields the form needs — photos are large data URLs and don't belong
    // in router history state.
    navigate('/birds/add', {
      state: {
        duplicateFrom: {
          id: bird.id,
          ring_number: bird.ring_number,
          color: bird.color,
          breed: bird.breed,
          year: bird.year,
          sire_id: bird.sire_id,
          dam_id: bird.dam_id
        },
        mode: 'duplicate'
      }
    });
  };

  const handleViewFullPedigree = async () => {
    if (!bird || openingFullPedigree) return;

    const previewTab = reservePdfTab();
    if (!previewTab) {
      setFullPedigreeError('Your browser blocked the PDF tab. Allow pop-ups for this site, then try again.');
      return;
    }

    setOpeningFullPedigree(true);
    setFullPedigreeError('');
    let viewUrl = '';
    try {
      const result = await PedigreeExporter.createPreviewForBirdId(bird.id);
      viewUrl = result.viewUrl;
      if (!loadPdfInReservedTab(previewTab, viewUrl)) {
        URL.revokeObjectURL(viewUrl);
        viewUrl = '';
        throw new Error('Could not load PDF in the reserved browser tab');
      }
    } catch (error) {
      console.error('Error opening full pedigree PDF:', error);
      try {
        previewTab.close();
      } catch (closeError) {
        // The browser may already own or have closed the reserved tab.
      }
      setFullPedigreeError('Could not open the full pedigree in a new tab. Please try again.');
    } finally {
      setOpeningFullPedigree(false);
    }
  };

  // Open the share dialog for paid users; send everyone else to the upgrade
  // prompt (sharing is a full-version feature).
  const openShare = () => {
    if (!canShare) { openPrompt && openPrompt(); return; }
    setShareEmail('');
    setShareError('');
    setShareDone('');
    setShareOpen(true);
  };

  const doShare = async () => {
    const email = shareEmail.trim();
    if (!email) { setShareError('Please enter the fancier\'s email address.'); return; }
    setShareBusy(true);
    setShareError('');
    try {
      const res = await DatabaseService.shareBird(bird.id, email);
      setShareDone(`Shared with ${res.recipient || email}. It's now in their "Shared with me" inbox.`);
    } catch (error) {
      // Server messages are user-facing ("no account with that email", etc.).
      setShareError(error.message || 'Could not share this bird. Please try again.');
    } finally {
      setShareBusy(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading bird profile...</Typography>
      </Box>
    );
  }

  if (!bird) {
    return (
      <Box textAlign="center" py={8}>
        <Typography variant="h6" color="text.secondary">
          Bird not found
        </Typography>
        <Button onClick={() => navigate('/birds')} sx={{ mt: 2 }}>
          Back to Birds
        </Button>
      </Box>
    );
  }

  // Name preferred, ring as fallback; ringLabel is the ring only when it isn't
  // already the displayed name (so it never shows twice).
  const { displayName, ringLabel } = getBirdListLabels(bird);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3} flexWrap="wrap" rowGap={1.5}>
        <IconButton aria-label="Back to birds" onClick={() => navigate('/birds')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" fontWeight="bold" flexGrow={1}>
          {displayName}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<PedigreeIcon />}
            onClick={() => navigate(`/pedigree/${bird.id}`)}
            aria-label="View preview pedigree"
            sx={{ minHeight: 44 }}
          >
            View Preview Pedigree
          </Button>
          {IS_WEB && fullPedigreeAvailable && (
            <Button
              variant="outlined"
              startIcon={openingFullPedigree
                ? <CircularProgress size={18} color="inherit" />
                : <FullPedigreeIcon />}
              onClick={handleViewFullPedigree}
              disabled={openingFullPedigree}
              aria-label="View full pedigree in a new tab"
              sx={{ minWidth: 190, minHeight: 44 }}
            >
              {openingFullPedigree ? 'Opening...' : 'View Full Pedigree'}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={handleDuplicateForPedigree}
          >
            Duplicate
          </Button>
          {IS_WEB && (
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={openShare}
            >
              Share
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/birds/${bird.id}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialog(true)}
          >
            Delete
          </Button>
        </Box>
      </Box>

      {fullPedigreeError && (
        <Alert severity="error" onClose={() => setFullPedigreeError('')} sx={{ mb: 3 }}>
          {fullPedigreeError}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                <Chip
                  icon={bird.sex === 'male' ? <MaleIcon /> : <FemaleIcon />}
                  label={bird.sex}
                  color={bird.sex === 'male' ? 'info' : 'secondary'}
                />
                {ringLabel && (
                  <Chip label={`Ring Details: ${ringLabel}`} variant="outlined" />
                )}
                {bird.breed && (
                  <Chip label={`Strain: ${bird.breed}`} variant="outlined" />
                )}
                {bird.color && (
                  <Chip label={`Color: ${bird.color}`} variant="outlined" />
                )}
                {bird.year && (
                  <Chip label={`Year: ${bird.year}`} variant="outlined" />
                )}
              </Box>

              {/* Photos */}
              {(bird.body_photo || bird.eye_photo) && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>
                    Photos
                  </Typography>
                  <Grid container spacing={2}>
                    {bird.body_photo && (
                      <Grid item xs={12} sm={6}>
                        <Card>
                          <Box
                            onClick={() => setLightbox({ image: bird.body_photo, title: `${displayName} - Body Photo` })}
                            sx={{
                              position: 'relative',
                              cursor: 'pointer',
                              '&:hover .zoom-hint': { opacity: 1 }
                            }}
                          >
                            <CardMedia
                              component="img"
                              height="300"
                              image={bird.body_photo}
                              alt={`${bird.name} - Body`}
                              sx={{ objectFit: 'cover' }}
                            />
                            <Box
                              className="zoom-hint"
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: 'rgba(0,0,0,0.5)',
                                color: '#fff',
                                borderRadius: '50%',
                                p: 0.5,
                                display: 'flex',
                                opacity: 0,
                                transition: 'opacity 0.2s'
                              }}
                            >
                              <ZoomInIcon />
                            </Box>
                          </Box>
                          <CardContent>
                            <Typography variant="subtitle2" textAlign="center">
                              Body Photo
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    {bird.eye_photo && (
                      <Grid item xs={12} sm={6}>
                        <Card>
                          <Box
                            onClick={() => setLightbox({ image: bird.eye_photo, title: `${displayName} - Eye Photo` })}
                            sx={{
                              position: 'relative',
                              cursor: 'pointer',
                              '&:hover .zoom-hint': { opacity: 1 }
                            }}
                          >
                            <CardMedia
                              component="img"
                              height="300"
                              image={bird.eye_photo}
                              alt={`${bird.name} - Eye`}
                              sx={{ objectFit: 'cover' }}
                            />
                            <Box
                              className="zoom-hint"
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: 'rgba(0,0,0,0.5)',
                                color: '#fff',
                                borderRadius: '50%',
                                p: 0.5,
                                display: 'flex',
                                opacity: 0,
                                transition: 'opacity 0.2s'
                              }}
                            >
                              <ZoomInIcon />
                            </Box>
                          </Box>
                          <CardContent>
                            <Typography variant="subtitle2" textAlign="center">
                              Eye Photo
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Parent Information */}
              {(bird.sire_id || bird.dam_id) && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>
                    Parents
                  </Typography>
                  <Grid container spacing={2}>
                    {bird.sire_id && (
                      <Grid item xs={12} sm={6}>
                        <ParentCard
                          role="Sire"
                          sex="male"
                          name={bird.sire_name}
                          ringNumber={bird.sire_ring_number}
                          onView={() => navigate(`/birds/${bird.sire_id}`)}
                        />
                      </Grid>
                    )}
                    {bird.dam_id && (
                      <Grid item xs={12} sm={6}>
                        <ParentCard
                          role="Dam"
                          sex="female"
                          name={bird.dam_name}
                          ringNumber={bird.dam_ring_number}
                          onView={() => navigate(`/birds/${bird.dam_id}`)}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Notes */}
              {bird.notes && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {bird.notes}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Race Results */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <RaceResults birdId={bird.id} readOnly={raceReadOnly} />
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Quick Actions */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<ExportIcon />}
                  onClick={() => navigate(`/pedigree/${bird.id}?export=pdf`)}
                >
                  Download Pedigree PDF
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<CopyIcon />}
                  onClick={handleDuplicateForPedigree}
                >
                  Duplicate for Entry
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Offspring */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Offspring ({offspring.length})
              </Typography>
              {offspring.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No offspring recorded yet.
                </Typography>
              ) : (
                <List dense>
                  {offspring.map((child, index) => {
                    const childLabels = getBirdListLabels(child);
                    return (
                    <React.Fragment key={child.id}>
                      <ListItem
                        button
                        onClick={() => navigate(`/birds/${child.id}`)}
                        sx={{ px: 0 }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{ width: 36, height: 36, ...sexAvatarSx(child.sex) }}
                          >
                            {child.sex === 'male' ? <MaleIcon /> : <FemaleIcon />}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={childLabels.displayName}
                          secondary={childLabels.ringLabel}
                        />
                      </ListItem>
                      {index < offspring.length - 1 && <Divider />}
                    </React.Fragment>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Bird</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{displayName}"? This action cannot be undone.
            {offspring.length > 0 && (
              <Box mt={2}>
                <Typography color="warning.main" variant="body2">
                  Warning: This bird has {offspring.length} offspring. Deleting this bird will remove the parent relationship for these birds.
                </Typography>
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share by email */}
      <Dialog open={shareOpen} onClose={() => !shareBusy && setShareOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Share "{displayName}"</DialogTitle>
        <DialogContent>
          {shareDone ? (
            <Alert severity="success" sx={{ mt: 1 }}>{shareDone}</Alert>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Send this pigeon and its full pedigree (ancestors and photos) to another
                fancier's account, like handing over a paper pedigree. They'll find it under
                "Shared with me" and can add it to their loft. They need a Pigeon Pedigree
                Manager account with this email.
              </DialogContentText>
              {shareError && <Alert severity="error" sx={{ mb: 2 }}>{shareError}</Alert>}
              <TextField
                autoFocus
                fullWidth
                type="email"
                label="Recipient's email"
                placeholder="fancier@example.com"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                disabled={shareBusy}
                onKeyDown={(e) => { if (e.key === 'Enter' && !shareBusy) doShare(); }}
                inputProps={{ maxLength: 200 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareOpen(false)} disabled={shareBusy}>
            {shareDone ? 'Close' : 'Cancel'}
          </Button>
          {!shareDone && (
            <Button
              variant="contained"
              onClick={doShare}
              disabled={shareBusy}
              startIcon={shareBusy ? <CircularProgress size={16} color="inherit" /> : <ShareIcon />}
            >
              {shareBusy ? 'Sharing…' : 'Share'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Photo Lightbox */}
      <Dialog
        open={Boolean(lightbox)}
        onClose={() => setLightbox(null)}
        maxWidth="lg"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          {lightbox?.title}
          <IconButton onClick={() => setLightbox(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: 'black' }}>
          {lightbox && (
            <Box
              component="img"
              src={lightbox.image}
              alt={lightbox.title}
              sx={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '80vh',
                m: '0 auto'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BirdProfile;
